import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import { fetchCaseDetail, fetchCaseReport, fetchCaseReportStatus, launchPostMortemJob } from '../api/cases'
import CaseMap from '../components/CaseMap'
import KeyTicketDetails from '../components/KeyTicketDetails'
import OwnershipTimeline from '../components/OwnershipTimeline'
import RoutingFactorsTable from '../components/RoutingFactorsTable'
import SkillsTab from '../components/SkillsTab'
import Avatar from '../components/Avatar'
import Icon from '../components/Icon'
import styles from './CaseDetails.module.css'

const TABS = [
  { key: 'response', label: 'Response Plan' },
  { key: 'diagnostic', label: 'Troubleshooting' },
  { key: 'resolution', label: 'Resolution' },
  // Hidden for now — re-enable by removing `hidden: true` once routing factors are ready.
  { key: 'routing', label: 'Routing Factors', hidden: true },
  { key: 'skills', label: 'Skills' },
]

// How often to re-check a report that is still generating.
const REPORT_POLL_MS = 10000

// P1/P2/… → dot color. Unknown priorities fall back to neutral grey.
function priorityClass(priority) {
  const p = String(priority || '').toUpperCase()
  if (p.startsWith('P1') || p.includes('CRITICAL')) return styles.dotP1
  if (p.startsWith('P2') || p.includes('HIGH')) return styles.dotP2
  if (p.startsWith('P3') || p.includes('MODERATE') || p.includes('MEDIUM')) return styles.dotP3
  return styles.dotP4
}

// minutes → "3d 7h 25m" (drops zero leading units). null/absent → em dash.
function formatDuration(minutes) {
  if (minutes == null || Number.isNaN(minutes)) return '—'
  const total = Math.max(0, Math.round(minutes))
  const d = Math.floor(total / 1440)
  const h = Math.floor((total % 1440) / 60)
  const m = total % 60
  const parts = []
  if (d) parts.push(`${d}d`)
  if (h || d) parts.push(`${h}h`)
  parts.push(`${m}m`)
  return parts.join(' ')
}

function formatOpened(iso) {
  if (!iso) return '—'
  const dt = new Date(iso)
  if (Number.isNaN(dt.getTime())) return iso
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function CaseDetails() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  // `loadedId` is the id the current data/error settled for; loading is derived from it
  // (true until this id's fetch resolves) so no state is set synchronously in the effect.
  const [loadedId, setLoadedId] = useState(null)
  const [tab, setTab] = useState('response')
  // Click-to-edit for the breadcrumb ticket number: commit navigates to that case.
  const [editingNumber, setEditingNumber] = useState(false)
  const [numberDraft, setNumberDraft] = useState('')
  // Last case that loaded successfully — survives :id changes so a 404 can offer
  // a link back to the case the user was viewing before.
  const [lastGoodId, setLastGoodId] = useState(null)
  // Post-mortem, independent of the page load above. `reportStatusId` is the id
  // the status settled for, so switching cases derives its way back to 'unknown'
  // rather than assigning state inside the effect — same idiom as `loadedId`.
  // Until it answers the button stays inert, rather than guessing and offering
  // the wrong action.
  const [reportStatusId, setReportStatusId] = useState(null)
  const [settledStatus, setSettledStatus] = useState('none')
  const [reportLoading, setReportLoading] = useState(false)   // PDF fetch in flight
  // Tagged with the case it belongs to, so a stale message cannot outlive it.
  const [reportErrorFor, setReportErrorFor] = useState(null)

  const reportStatus = reportStatusId === id ? settledStatus : 'unknown'
  const reportError = reportErrorFor?.id === id ? reportErrorFor.message : null

  function showReportError(message) {
    setReportErrorFor(message ? { id, message } : null)
  }

  useEffect(() => {
    let active = true
    fetchCaseDetail(id)
      .then(d => { if (active) { setData(d); setError(null); setLastGoodId(id) } })
      .catch(e => { if (active) { setData(null); setError(e) } })
      .finally(() => { if (active) setLoadedId(id) })
    return () => { active = false }
  }, [id])

  const loading = loadedId !== id

  // Which post-mortem button to show. Re-checked whenever the case changes.
  useEffect(() => {
    let active = true
    fetchCaseReportStatus(id)
      .then(r => {
        if (!active) return
        const next = r?.status || 'none'
        setSettledStatus(next)
        setReportStatusId(id)
        if (next === 'error') {
          setReportErrorFor({ id, message: r.message || 'Report generation failed.' })
        }
      })
      // A status read that fails should not strand the button: fall back to
      // offering generation rather than leaving it permanently inert.
      .catch(() => { if (active) { setSettledStatus('none'); setReportStatusId(id) } })
    return () => { active = false }
  }, [id])

  // While a job is running, keep asking until it reaches a terminal state.
  useEffect(() => {
    if (reportStatus !== 'generating') return undefined
    let active = true
    const timer = setInterval(() => {
      fetchCaseReportStatus(id)
        .then(r => {
          if (!active || !r?.status) return
          setSettledStatus(r.status)
          setReportStatusId(id)
          if (r.status === 'error') {
            setReportErrorFor({ id, message: r.message || 'Report generation failed.' })
          }
        })
        // A blip mid-run is not a failed report — keep polling.
        .catch(() => {})
    }, REPORT_POLL_MS)
    return () => { active = false; clearInterval(timer) }
  }, [reportStatus, id])

  function beginEditNumber() {
    setNumberDraft(data.number)
    setEditingNumber(true)
  }

  function commitNumber() {
    const next = numberDraft.trim()
    setEditingNumber(false)
    if (next && next !== id) navigate(`/cases/${encodeURIComponent(next)}`)
  }

  function onNumberKeyDown(e) {
    if (e.key === 'Enter') { e.preventDefault(); commitNumber() }
    else if (e.key === 'Escape') { e.preventDefault(); setEditingNumber(false) }
  }

  /**
   * Start a post-mortem generation job for this case.
   *
   * Flips to 'generating' immediately rather than waiting for the status
   * endpoint to say so: the Reports row that carries that status is not written
   * at launch, so a poll fired right now would answer 'none' and re-offer a
   * button the user just pressed. The polling effect takes over from here.
   */
  async function launchPostMortem() {
    showReportError(null)
    setSettledStatus('generating')
    setReportStatusId(id)
    try {
      await launchPostMortemJob(id)
    } catch (e) {
      setSettledStatus('error')
      setReportStatusId(id)
      showReportError(e.detail || e.message || 'Could not start report generation.')
    }
  }

  // One button, three actions — see reportButton() for the matching labels.
  function onPostMortemClick() {
    if (reportStatus === 'done') return openPostMortem()
    if (reportStatus === 'none' || reportStatus === 'error') return launchPostMortem()
    // 'generating' — the poll is already watching it; 'unknown' — not yet known.
  }

  /**
   * Fetch the case's post-mortem PDF and show it in a new tab.
   *
   * The endpoint needs an Authorization header, so we cannot simply point a tab
   * at its URL — we fetch the bytes and hand the tab a blob: URL instead.
   *
   * Two browser behaviours shape the sequence below:
   *   • a window.open() after an `await` has lost the user-gesture context and
   *     gets swallowed by popup blockers, so the tab is opened up front, while
   *     the click is still on the stack, and navigated once the bytes land;
   *   • 'noopener' makes window.open() return null, which would leave us no
   *     handle to navigate — so it is omitted here. The tab only ever loads a
   *     same-origin blob of our own, so there is no third-party opener to deny.
   */
  async function openPostMortem() {
    if (reportLoading) return
    setReportLoading(true)
    showReportError(null)

    const tab = window.open('', '_blank')
    if (tab) {
      // Otherwise the tab sits blank for however long the fetch takes and reads
      // as a broken click.
      try { tab.document.write('Loading post-mortem…') } catch { /* not writable */ }
    }

    let url
    try {
      const blob = await fetchCaseReport(id)
      url = URL.createObjectURL(blob)
      if (tab) tab.location.href = url
      else window.open(url, '_blank', 'noopener')   // popup blocked; best effort
    } catch (e) {
      if (tab) tab.close()
      // A 404 here means the row said 'done' but the bytes are gone — put the
      // button back to offering generation rather than looping on a dead link.
      if (e.status === 404) {
        setSettledStatus('none')
        setReportStatusId(id)
      }
      showReportError(
        e.status === 404
          ? 'No post-mortem has been generated for this case yet.'
          : (e.detail || e.message || 'Could not open the post-mortem.'),
      )
    } finally {
      setReportLoading(false)
      // Revoking immediately races the tab's navigation and lands it on a blank
      // page; hold the blob long enough for the viewer to have read it.
      if (url) setTimeout(() => URL.revokeObjectURL(url), 60000)
    }
  }

  if (loading) {
    return <div className={styles.state}>Loading case…</div>
  }
  if (error) {
    const notFound = error.status === 404
    const prevId = lastGoodId
    return (
      <div className={styles.state}>
        <p>{notFound ? `Case ${id} was not found.` : (error.detail || error.message || 'Failed to load case.')}</p>
        <div className={styles.stateActions}>
          <button type="button" className={styles.backLink} onClick={() => navigate('/cases')}>← Back to Cases</button>
          {prevId && prevId !== id && (
            <button type="button" className={styles.backLink} onClick={() => navigate(`/cases/${encodeURIComponent(prevId)}`)}>← Back to Previous Case</button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      {/* ── Breadcrumb + title ─────────────────────────── */}
      <div className={styles.breadcrumb}>
        <button type="button" className={styles.crumbLink} onClick={() => navigate('/cases')}>Cases</button>
        <span className={styles.crumbSep}>›</span>
        {editingNumber ? (
          <input
            type="text"
            className={styles.crumbInput}
            value={numberDraft}
            autoFocus
            onFocus={e => e.target.select()}
            onChange={e => setNumberDraft(e.target.value)}
            onKeyDown={onNumberKeyDown}
            onBlur={() => setEditingNumber(false)}
            aria-label="Go to ticket number"
          />
        ) : (
          <button
            type="button"
            className={styles.crumbCurrent}
            onClick={beginEditNumber}
            title="Click to open a different ticket"
          >
            {data.number}
          </button>
        )}
      </div>

      <div className={styles.titleRow}>
        <h1 className={styles.title}>
          <span className={styles.titleId}>{data.number}</span>
          {data.title}
        </h1>
        <PostMortemButton
          status={reportStatus}
          opening={reportLoading}
          onClick={onPostMortemClick}
        />
      </div>

      {reportError && (
        <div className={styles.reportError} role="alert">{reportError}</div>
      )}

      {/* ── Two-column layout ──────────────────────────── */}
      <div className={styles.layout}>
        <div className={styles.main}>
          {/* Summary */}
          <section className={styles.summaryCard}>
            <div className={styles.summaryMeta}>
              <span className={styles.metaLabel}>SUMMARY</span>
              <span className={`${styles.dot} ${priorityClass(data.priority)}`} />
              <span className={styles.priority}>{data.priority || '—'}</span>
              <span className={styles.metaDivider}>·</span>
              <span className={styles.opened}>Opened {formatOpened(data.created_at)}</span>
            </div>
            {data.summary && <p className={styles.summaryText}>{data.summary}</p>}
            <div className={styles.facts}>
              <Fact label="Resolving Agent" value={data.resolving_agent} avatar />
              <Fact label="Time to Resolve" value={formatDuration(data.time_to_resolve)} />
              <Fact label="Net Time to Resolve" value={formatDuration(data.net_time_to_resolve)} />
              <Fact label="Reassignments" value={`${data.reassigned_count ?? 0} hops`} />
            </div>
          </section>

          {/* Relationship Map */}
          <section className={styles.mapCard}>
            <div className={styles.mapHead}>
              <h2 className={styles.cardHeading}>Diagnostic Chain</h2>
            </div>
            <CaseMap
              symptoms={data.symptoms}
              problems={data.problems}
              resolutionCode={data.root_cause}
            />
          </section>

          {/* Tabbed detail */}
          <section className={styles.tabCard}>
            <div className={styles.tabs} role="tablist">
              {TABS.filter(t => !t.hidden).map(t => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.key}
                  className={`${styles.tab} ${tab === t.key ? styles.tabActive : ''}`}
                  onClick={() => setTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className={styles.tabBody}>
              {tab === 'response' && <ResponsePlan plan={data.response_plan} />}
              {tab === 'diagnostic' && <DiagnosticPlan historySummary={data.history_summary} chatLogSummary={data.chat_log_summary} />}
              {tab === 'resolution' && <ResolutionDetails data={data} />}
              {tab === 'routing' && <RoutingFactorsTable factors={data.routing_factors} />}
              {tab === 'skills' && <SkillsTab skills={data.skills} />}
            </div>
          </section>
        </div>

        {/* Right rail */}
        <div className={styles.side}>
          <div className={styles.rail}>
            <KeyTicketDetails
              customer={data.account}
              complexity={data.complexity}
              createdBy={data.created_by}
              categories={data.categories}
              csat={data.csat_score != null ? Number(data.csat_score) : null}
              resolutionQuality={data.resolution_quality_score}
              resolutionQualityCaption={data.resolution_quality_caption}
              friction={data.ownership_friction_score}
              frictionCaption={data.ownership_friction_caption}
            />
            <OwnershipTimeline events={data.ownership_timeline} />
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * The case retrospective button, in whichever of its three states applies.
 *
 *   none / error → "Generate Case Retrospective"  launches a job (error = retry)
 *   generating   → "Generating Report"     spinner, inert while the page polls
 *   done         → "Show Case Retrospective"      opens the PDF in a new tab
 *
 * An error keeps the generate action rather than dead-ending: the message is
 * shown in the banner below, and the obvious next move is to try again.
 */
function PostMortemButton({ status, opening, onClick }) {
  const generating = status === 'generating'
  const inert = generating || status === 'unknown' || opening

  let label = 'Generate Case Retrospective'
  if (generating) label = 'Generating Report'
  else if (status === 'done') label = opening ? 'Opening…' : 'Show Case Retrospective'

  return (
    <button
      type="button"
      className={styles.postMortem}
      onClick={onClick}
      disabled={inert}
      aria-busy={generating || opening}
      aria-live="polite"
    >
      {generating ? <SpinnerIcon /> : <DocIcon />} {label}
    </button>
  )
}

function Fact({ label, value, avatar }) {
  const empty = value == null || value === ''
  return (
    <div className={styles.fact}>
      <span className={styles.factLabel}>{label}</span>
      <span className={styles.factValue}>
        {avatar && !empty && <Avatar name={value} size={22} />}
        {empty ? '—' : value}
      </span>
    </div>
  )
}

// response_plan is a markdown-text field; absent/null tickets get a placeholder.
function ResponsePlan({ plan }) {
  if (!plan) return <p className={styles.tabEmpty}>No Response Plan for this Ticket</p>
  return (
    <div className={styles.responsePlan}>
      <ReactMarkdown>{plan}</ReactMarkdown>
    </div>
  )
}

// history_summary / chat_log_summary are markdown fields; sub-tabs appear only when data is present.
function DiagnosticPlan({ historySummary, chatLogSummary }) {
  const subTabs = [
    historySummary && { key: 'caseHistory', label: 'History Summary', content: historySummary },
    chatLogSummary && { key: 'chatHistory', label: 'Transcription Summary', content: chatLogSummary },
  ].filter(Boolean)

  const [activeSubTab, setActiveSubTab] = useState(() => subTabs[0]?.key ?? null)

  if (subTabs.length === 0) {
    return <p className={styles.tabEmpty}>No Diagnostic Plan for this Ticket</p>
  }

  const current = subTabs.find(t => t.key === activeSubTab) ?? subTabs[0]

  return (
    <div>
      {subTabs.length > 1 && (
        <div className={styles.subTabs} role="tablist">
          {subTabs.map(t => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={current.key === t.key}
              className={`${styles.subTab} ${current.key === t.key ? styles.subTabActive : ''}`}
              onClick={() => setActiveSubTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
      <div className={styles.responsePlan}>
        <ReactMarkdown>{current.content}</ReactMarkdown>
      </div>
    </div>
  )
}

// Resolving agent + resolution notes (sourced from root_cause_detail).
function ResolutionDetails({ data }) {
  const hasNotes = Boolean(data.resolution_notes)
  return (
    <div className={styles.rootCause}>
      {data.resolving_agent && (
        <div className={styles.rcAgent}>
          <Avatar name={data.resolving_agent} size={30} />
          <span className={styles.rcAgentName}>{data.resolving_agent}</span>
        </div>
      )}
      {hasNotes ? (
        <div className={styles.rcNotesBlock}>
          <span className={styles.rcAgentSub}>
            Resolution notes{data.resolved_at ? ` · ${formatOpened(data.resolved_at)}` : ''}
          </span>
          <p className={styles.rcNotes}>{data.resolution_notes}</p>
        </div>
      ) : (
        <p className={styles.tabEmpty}>No resolution notes recorded.</p>
      )}
    </div>
  )
}

// Circular two-arrow refresh glyph; the spin comes from CSS so it can be
// switched off under prefers-reduced-motion.
function SpinnerIcon() {
  return <Icon name="refresh" className={styles.spinning} />
}

function DocIcon() {
  return <Icon name="cases" />
}
